<template>
  <div class="user-settings-section">
    <div v-if="statusMessage" class="alert py-2 mb-3" :class="statusMessageClass">
      {{ statusMessage }}
    </div>

    <div v-if="section === 'general'" class="settings-panel">
      <div class="settings-row">
        <div>
          <strong>Cache local</strong>
          <small>Efface les caches de navigation et les préférences locales non essentielles.</small>
        </div>
        <button
          type="button"
          class="btn btn-outline-danger btn-sm"
          :disabled="isClearingCache"
          @click="clearLocalCache"
        >
          <i class="fa" :class="isClearingCache ? 'fa-spinner fa-spin' : 'fa-trash-can'"></i>
          <span>Vider le cache</span>
        </button>
      </div>

      <section v-if="showAppearanceSettings" class="theme-settings" aria-labelledby="adminThemeTitle">
        <div class="settings-row theme-settings-heading">
          <div>
            <strong id="adminThemeTitle">Apparence du backoffice</strong>
            <small>Choisissez une palette pour les pages d’administration.</small>
          </div>
        </div>

        <div class="theme-options">
          <label
            v-for="theme in adminThemeOptions"
            :key="theme.key"
            class="theme-option"
            :class="{ active: form.appearance.admin_theme === theme.key }"
          >
            <input
              v-model="form.appearance.admin_theme"
              class="theme-option-input"
              name="adminTheme"
              type="radio"
              :value="theme.key"
            />
            <span class="theme-option-swatch" :style="themeSwatchStyle(theme)" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </span>
            <span class="theme-option-copy">
              <strong>{{ theme.label }}</strong>
              <small>{{ theme.description }}</small>
            </span>
            <i v-if="form.appearance.admin_theme === theme.key" class="fa fa-check theme-option-check"></i>
          </label>
        </div>
      </section>

      <label v-if="cashierMode" class="settings-check-row" for="showCashierClientCard">
        <PrimevueCheckbox
          v-model="form.general.show_cashier_client_card"
          inputId="showCashierClientCard"
          binary
        />
        <span>Afficher le card client dans la caisse</span>
      </label>

      <label v-if="isCashier" class="settings-check-row" for="customerDisplayEnabled">
        <PrimevueCheckbox
          v-model="localCustomerDisplaySettings.enabled"
          inputId="customerDisplayEnabled"
          binary
        />
        <span class="settings-check-copy">
          <strong>Activer l’écran client de caisse</strong>
          <small>Permet d’ouvrir une fenêtre dédiée au panier sur le deuxième écran du poste.</small>
        </span>
      </label>

      <label v-if="isCashier" class="settings-check-row" for="cashDrawerEnabled">
        <PrimevueCheckbox
          v-model="form.general.cash_drawer_enabled"
          inputId="cashDrawerEnabled"
          binary
        />
        <span class="settings-check-copy">
          <strong>Activer le tiroir-caisse</strong>
          <small>Essaie d’ouvrir automatiquement le tiroir au démarrage du billetage.</small>
        </span>
      </label>
    </div>

    <div v-else-if="section === 'printing'" class="settings-panel">
      <div class="printer-tab-heading">
        <div>
          <strong>Impression thermique</strong>
          <small>Les réglages du pont local restent enregistrés uniquement sur ce poste.</small>
        </div>
        <button
          type="button"
          class="printer-info-button"
          title="Aide sur les paramètres d’impression"
          aria-label="Aide sur les paramètres d’impression"
          :aria-expanded="printerHelpVisible ? 'true' : 'false'"
          aria-controls="user-settings-printer-help"
          @click="printerHelpVisible = !printerHelpVisible"
        >
          i
        </button>
      </div>

      <section v-if="printerHelpVisible" id="user-settings-printer-help" class="printer-help-panel">
        <div>
          <h6>Comment ça fonctionne</h6>
          <p>
            L’impression thermique envoie le ticket vers un pont local installé sur le poste de caisse.
            La SPA contacte ce pont avec l’URL et le token ci-dessous, puis le pont transmet la commande à
            l’imprimante ESC/POS ou au tiroir-caisse connecté.
          </p>
        </div>

        <div>
          <h6>Rôle des paramètres</h6>
          <ul>
            <li><strong>Activer l’impression thermique</strong> utilise l’imprimante ESC/POS au lieu de l’impression navigateur.</li>
            <li><strong>URL du pont local</strong> correspond au service installé sur l’ordinateur, par exemple <code>http://127.0.0.1:24183</code>.</li>
            <li><strong>Token local</strong> est la clé que le pont vérifie avant d’accepter une impression.</li>
            <li><strong>Impression navigateur en secours</strong> lance l’impression classique si le pont est indisponible.</li>
          </ul>
        </div>

        <div>
          <h6>Connecter un périphérique</h6>
          <ol>
            <li>Branchez l’imprimante thermique en USB, réseau ou Bluetooth, puis vérifiez qu’elle imprime depuis le système.</li>
            <li>Lancez Suite241 Print Bridge sur le poste de caisse et gardez-le ouvert.</li>
            <li>Copiez l’URL et le token fournis par le pont dans ces champs.</li>
            <li>Activez l’impression thermique, enregistrez, puis utilisez <strong>Tester le pont</strong>.</li>
            <li>Envoyez un <strong>Ticket test</strong>. Si un tiroir-caisse est relié au port DK de l’imprimante, testez aussi <strong>Ouvrir tiroir</strong>.</li>
          </ol>
        </div>
      </section>

      <label class="settings-check-row" for="thermalEnabled">
        <PrimevueCheckbox
          v-model="form.printing.thermal_enabled"
          inputId="thermalEnabled"
          binary
        />
        <span>Activer l’impression thermique ESC/POS</span>
      </label>

      <div class="settings-grid">
        <label>
          <span>URL du pont local</span>
          <PrimevueInputText
            v-model.trim="localPrinterSettings.bridge_url"
            type="text"
            class="w-100"
            placeholder="http://127.0.0.1:24183"
          />
        </label>

        <label>
          <span>Token local</span>
          <PrimevueInputText
            v-model.trim="localPrinterSettings.bridge_token"
            type="password"
            class="w-100"
            autocomplete="off"
          />
        </label>
      </div>

      <label class="settings-check-row" for="fallbackBrowser">
        <PrimevueCheckbox
          v-model="form.printing.fallback_browser"
          inputId="fallbackBrowser"
          binary
        />
        <span>Utiliser l’impression navigateur si le pont local est indisponible</span>
      </label>

      <label v-if="cashierMode" class="settings-check-row" for="autoPrintAfterSale">
        <PrimevueCheckbox
          v-model="form.printing.auto_print_after_sale"
          inputId="autoPrintAfterSale"
          binary
        />
        <span>Imprimer automatiquement le ticket après la vente</span>
      </label>

      <div v-if="printerSettingsMessage" class="alert py-2 mt-2 mb-0" :class="printerSettingsMessageClass">
        {{ printerSettingsMessage }}
      </div>

      <div class="printer-actions">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="isTestingPrinter"
          @click="testPrinterBridge"
        >
          <i class="fa fa-plug"></i>
          <span>Tester le pont</span>
        </button>
        <button
          type="button"
          class="btn btn-outline-primary btn-sm"
          :disabled="isTestingPrinter"
          @click="printPrinterTest"
        >
          <i class="fa fa-receipt"></i>
          <span>Ticket test</span>
        </button>
        <button
          type="button"
          class="btn btn-outline-warning btn-sm"
          :disabled="isTestingPrinter"
          @click="openPrinterDrawer"
        >
          <i class="fa fa-cash-register"></i>
          <span>Ouvrir tiroir</span>
        </button>
      </div>
    </div>

    <div v-else class="settings-panel">
      <label class="settings-check-row" for="hideProcessedProducts">
        <PrimevueCheckbox
          v-model="form.inventory.hide_processed_products"
          inputId="hideProcessedProducts"
          binary
        />
        <span>Masquer les cartes des produits traités pendant l’inventaire</span>
      </label>

      <label class="settings-check-row" for="showOneRayon">
        <PrimevueCheckbox
          v-model="form.inventory.show_one_rayon_at_time"
          inputId="showOneRayon"
          binary
        />
        <span>Afficher un rayon à la fois pendant l’inventaire</span>
      </label>
    </div>

    <div class="settings-section-footer">
      <button
        type="button"
        class="btn btn-primary btn-sm"
        :disabled="isSaving"
        @click="saveSettings"
      >
        <i class="fa" :class="isSaving ? 'fa-spinner fa-spin' : 'fa-check'"></i>
        <span>{{ isSaving ? 'Enregistrement...' : 'Enregistrer' }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import {
  AUTH_TOKEN_STORAGE_KEY,
  AUTH_USER_STORAGE_KEY,
  INVENTORY_DRAFT_STORAGE_PREFIX,
  SHOP_ID_STORAGE_KEY,
  SHOP_INFO_STORAGE_KEY,
} from '@/config/app';
import {
  DEFAULT_THERMAL_PRINTER_SETTINGS,
  THERMAL_PRINTER_SETTINGS_KEY,
  loadThermalPrinterSettings,
  openThermalDrawer,
  printThermalTest,
  saveLocalPrinterBridgeSettings,
  testThermalBridge,
} from '@/services/thermalPrinter';
import { ADMIN_THEME_OPTIONS } from '@/support/adminThemes';
import {
  clearBrowserCachesForLatestVersion,
  reloadToLatestSpaVersion,
} from '@/support/appCacheRefresh';
import {
  CUSTOMER_DISPLAY_SETTINGS_KEY,
  loadCustomerDisplaySettings,
  saveCustomerDisplaySettings,
} from '@/support/customerDisplay';
import { DEFAULT_USER_SETTINGS, USER_SETTINGS_STORAGE_PREFIX } from '@/store/modules/userSettings';

const CASH_REGISTER_SESSION_STORAGE_KEY = 'suite241.cashRegisterSession';

const cloneSettings = (settings) => JSON.parse(JSON.stringify(settings || DEFAULT_USER_SETTINGS));

export default {
  name: 'UserSettingsSection',
  props: {
    section: {
      type: String,
      default: 'general',
      validator: (value) => ['general', 'printing', 'inventory'].includes(value),
    },
    showAppearanceSettings: {
      type: Boolean,
      default: true,
    },
    cashierMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: cloneSettings(DEFAULT_USER_SETTINGS),
      localPrinterSettings: loadThermalPrinterSettings(),
      localCustomerDisplaySettings: loadCustomerDisplaySettings(),
      printerHelpVisible: false,
      printerSettingsMessage: '',
      printerSettingsMessageClass: 'alert-info',
      statusMessage: '',
      statusMessageClass: 'alert-info',
      adminThemeOptions: ADMIN_THEME_OPTIONS,
      isTestingPrinter: false,
      isClearingCache: false,
    };
  },
  computed: {
    isCashier() {
      return this.$store?.getters?.['auth/currentUser']?.role === 'caissier';
    },
    isSaving() {
      return this.$store.getters['userSettings/isSaving'];
    },
  },
  watch: {
    section() {
      this.prepareSection();
    },
  },
  mounted() {
    this.prepareSection();
  },
  methods: {
    async prepareSection() {
      this.statusMessage = '';
      this.printerSettingsMessage = '';
      this.printerHelpVisible = false;
      this.localPrinterSettings = loadThermalPrinterSettings();
      this.localCustomerDisplaySettings = loadCustomerDisplaySettings();
      this.form = cloneSettings(this.$store.getters['userSettings/settings']);

      try {
        const settings = await this.$store.dispatch('userSettings/load');
        this.form = cloneSettings(settings);
      } catch (error) {
        this.statusMessage = 'Impossible de synchroniser les paramètres. Les valeurs locales restent affichées.';
        this.statusMessageClass = 'alert-warning';
      }
    },

    printerSettingsForAction() {
      return {
        ...DEFAULT_THERMAL_PRINTER_SETTINGS,
        ...this.localPrinterSettings,
        thermal_enabled: !!this.form.printing.thermal_enabled,
        fallback_browser: this.form.printing.fallback_browser !== false,
      };
    },

    saveLocalPrinterBridge() {
      this.localPrinterSettings = saveLocalPrinterBridgeSettings({
        bridge_url: this.localPrinterSettings.bridge_url,
        bridge_token: this.localPrinterSettings.bridge_token,
      });
    },

    async saveSettings() {
      this.statusMessage = '';
      this.printerSettingsMessage = '';

      try {
        if (this.section === 'printing') {
          this.saveLocalPrinterBridge();
        }

        if (this.section === 'general') {
          this.localCustomerDisplaySettings = saveCustomerDisplaySettings(this.localCustomerDisplaySettings);
        }

        const currentSettings = cloneSettings(this.$store.getters['userSettings/settings']);
        const formSettings = cloneSettings(this.form);
        const payload = {
          ...currentSettings,
          [this.section]: formSettings[this.section],
        };

        if (this.section === 'general' && this.showAppearanceSettings) {
          payload.appearance = formSettings.appearance;
        }

        const saved = await this.$store.dispatch('userSettings/save', payload);
        this.form = cloneSettings(saved);
        this.statusMessage = 'Paramètres enregistrés.';
        this.statusMessageClass = 'alert-success';
      } catch (error) {
        this.statusMessage = 'Impossible d’enregistrer les paramètres.';
        this.statusMessageClass = 'alert-danger';
      }
    },

    async clearLocalCache() {
      this.isClearingCache = true;
      this.statusMessage = '';

      try {
        this.preserveAndClearLocalStorage();
        await clearBrowserCachesForLatestVersion();

        this.statusMessage = 'Cache vidé. La dernière version de l’application va être rechargée.';
        this.statusMessageClass = 'alert-success';
        window.setTimeout(() => {
          reloadToLatestSpaVersion();
        }, 450);
      } catch (error) {
        this.statusMessage = 'Impossible de vider complètement le cache local.';
        this.statusMessageClass = 'alert-warning';
        this.isClearingCache = false;
      } finally {
        if (this.statusMessageClass !== 'alert-success') {
          this.isClearingCache = false;
        }
      }
    },

    preserveAndClearLocalStorage() {
      const exactKeys = [
        AUTH_TOKEN_STORAGE_KEY,
        AUTH_USER_STORAGE_KEY,
        SHOP_ID_STORAGE_KEY,
        SHOP_INFO_STORAGE_KEY,
        CASH_REGISTER_SESSION_STORAGE_KEY,
        THERMAL_PRINTER_SETTINGS_KEY,
        CUSTOMER_DISPLAY_SETTINGS_KEY,
      ];
      const prefixes = [
        INVENTORY_DRAFT_STORAGE_PREFIX,
        USER_SETTINGS_STORAGE_PREFIX,
      ];
      const preserved = [];

      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);
        if (!key) continue;

        if (exactKeys.includes(key) || prefixes.some((prefix) => key.startsWith(prefix))) {
          preserved.push([key, window.localStorage.getItem(key)]);
        }
      }

      window.localStorage.clear();

      preserved.forEach(([key, value]) => {
        if (value !== null) {
          window.localStorage.setItem(key, value);
        }
      });
    },

    themeSwatchStyle(theme) {
      return {
        '--theme-primary': theme.primary,
        '--theme-nav': theme.navBg,
        '--theme-accent': theme.accent,
        '--theme-soft': theme.accentSoft,
      };
    },

    async runPrinterSettingsAction(action, successMessage) {
      this.saveLocalPrinterBridge();
      this.isTestingPrinter = true;
      this.printerSettingsMessage = '';

      try {
        const result = await action(this.printerSettingsForAction());
        this.printerSettingsMessage = typeof successMessage === 'function'
          ? successMessage(result)
          : successMessage;
        this.printerSettingsMessageClass = 'alert-success';
      } catch (error) {
        this.printerSettingsMessage = error?.message || 'Action imprimante impossible.';
        this.printerSettingsMessageClass = 'alert-danger';
      } finally {
        this.isTestingPrinter = false;
      }
    },

    async testPrinterBridge() {
      await this.runPrinterSettingsAction(
        testThermalBridge,
        (result) => `Pont local connecté (${result.target || 'imprimante configurée'}).`
      );
    },

    async printPrinterTest() {
      await this.runPrinterSettingsAction(printThermalTest, 'Ticket test envoyé.');
    },

    async openPrinterDrawer() {
      await this.runPrinterSettingsAction(openThermalDrawer, 'Commande tiroir envoyée.');
    },
  },
};
</script>

<style scoped>
.user-settings-section {
  color: #0f172a;
}

.settings-panel {
  display: grid;
  gap: 1rem;
}

.settings-row,
.printer-tab-heading {
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  padding: 0.85rem;
}

.settings-row strong,
.printer-tab-heading strong {
  display: block;
  font-size: 0.9rem;
  font-weight: 900;
}

.settings-row small,
.printer-tab-heading small {
  color: #64748b;
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  margin-top: 0.15rem;
}

.settings-check-row {
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 0.65rem;
  margin: 0;
  padding: 0.85rem;
}

.settings-check-row span {
  font-size: 0.88rem;
  font-weight: 800;
}

.settings-check-copy {
  display: grid;
  gap: 0.15rem;
}

.settings-check-copy small {
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 700;
  line-height: 1.35;
}

.theme-settings {
  display: grid;
  gap: 0.75rem;
}

.theme-settings-heading {
  background: #f8fafc;
}

.theme-options {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.theme-option {
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  display: grid;
  gap: 0.7rem;
  grid-template-columns: auto minmax(0, 1fr) auto;
  margin: 0;
  min-height: 82px;
  padding: 0.75rem;
  position: relative;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.12s ease;
}

.theme-option:hover {
  border-color: var(--theme-primary, #0d6efd);
  box-shadow: 0 0.45rem 1rem rgba(15, 23, 42, 0.08);
}

.theme-option.active {
  border-color: var(--theme-primary, #0d6efd);
  box-shadow: 0 0 0 3px var(--theme-soft, rgba(13, 110, 253, 0.12));
  transform: translateY(-1px);
}

.theme-option-input {
  height: 1px;
  opacity: 0;
  position: absolute;
  width: 1px;
}

.theme-option-swatch {
  background: var(--theme-nav, #0d6efd);
  border-radius: 8px;
  display: grid;
  gap: 0.22rem;
  height: 48px;
  overflow: hidden;
  padding: 0.28rem;
  width: 54px;
}

.theme-option-swatch span {
  border-radius: 999px;
  display: block;
}

.theme-option-swatch span:nth-child(1) {
  background: rgba(255, 255, 255, 0.85);
  height: 8px;
  width: 75%;
}

.theme-option-swatch span:nth-child(2) {
  background: var(--theme-primary, #0d6efd);
  height: 18px;
  width: 100%;
}

.theme-option-swatch span:nth-child(3) {
  background: var(--theme-accent, #0b508c);
  height: 8px;
  width: 58%;
}

.theme-option-copy {
  display: grid;
  gap: 0.18rem;
  min-width: 0;
}

.theme-option-copy strong {
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 900;
  line-height: 1.2;
}

.theme-option-copy small {
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.35;
}

.theme-option-check {
  align-items: center;
  background: var(--theme-primary, #0d6efd);
  border-radius: 999px;
  color: #ffffff;
  display: inline-flex;
  font-size: 0.72rem;
  height: 22px;
  justify-content: center;
  width: 22px;
}

.settings-grid {
  display: grid;
  gap: 0.9rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.settings-grid label {
  display: grid;
  gap: 0.35rem;
}

.settings-grid span {
  color: #334155;
  font-size: 0.78rem;
  font-weight: 900;
}

.printer-info-button {
  align-items: center;
  background: #eef4ff;
  border: 1px solid #cfe0ff;
  border-radius: 50%;
  color: #155cff;
  display: inline-flex;
  flex: 0 0 30px;
  font-size: 0.92rem;
  font-weight: 900;
  height: 30px;
  justify-content: center;
  line-height: 1;
  width: 30px;
}

.printer-info-button:hover,
.printer-info-button:focus {
  background: #155cff;
  border-color: #155cff;
  color: #ffffff;
}

.printer-help-panel {
  background: #f8fbff;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  display: grid;
  gap: 0.85rem;
  padding: 0.95rem;
}

.printer-help-panel h6 {
  color: #0f172a;
  font-size: 0.86rem;
  font-weight: 900;
  margin-bottom: 0.35rem;
}

.printer-help-panel p,
.printer-help-panel li {
  color: #475569;
  font-size: 0.82rem;
  line-height: 1.55;
}

.printer-help-panel p {
  margin-bottom: 0;
}

.printer-help-panel ul,
.printer-help-panel ol {
  display: grid;
  gap: 0.4rem;
  margin-bottom: 0;
  padding-left: 1.15rem;
}

.printer-help-panel code {
  background: #ffffff;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  color: #155cff;
  font-size: 0.78rem;
  padding: 0.08rem 0.26rem;
}

.printer-actions,
.settings-section-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  justify-content: flex-end;
}

.printer-actions {
  justify-content: flex-start;
}

.printer-actions .btn,
.settings-section-footer .btn,
.settings-row .btn {
  align-items: center;
  display: inline-flex;
  gap: 0.4rem;
}

.settings-section-footer {
  border-top: 1px solid #e2e8f0;
  margin-top: 1.25rem;
  padding-top: 1rem;
}

@media (max-width: 575.98px) {
  .settings-row,
  .printer-tab-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .settings-grid {
    grid-template-columns: 1fr;
  }
}
</style>
